import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useInventoryForCleaner } from "@/hooks/useCleaner";
import { useInventoryRoomsForCleaner } from "@/hooks/useInventoryRoomsForCleaner";
import { usePhotoGroups } from "@/hooks/usePhotoGroups";
import { getInStockCount } from "@/lib/inventoryQuantity";
import { parseGuestSetupState } from "@/types/lifecycle";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "@/hooks/use-toast";
import { Camera, Loader2, Users, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import { CleanerMediaLightbox } from "@/components/cleaner/CleanerMediaLightbox";
import {
  computeLeaveOutQty,
  formatWhereLine,
  shortLeaveOutLabel,
} from "@/lib/leaveOut";
import { effectiveLeaveOutRule } from "@/lib/guestAmenityStaging";
import { resolveLeaveOut } from "@/lib/leaveOutPlan";
import type { Json } from "@/integrations/supabase/types";
import type { GuestAmenityStagingLine } from "@/types/checklist";

const UNASSIGNED_KEY = "__unassigned__";

type GuestItem = {
  id: string;
  item_name: string;
  room_id?: string | null;
  photo_url?: string | null;
  inventory_scope?: string | null;
  quantity_type?: string | null;
  in_stock_qty?: number | null;
  current_count?: number | null;
  current_value?: number | null;
};

export interface GuestAmenitySetupStepProps {
  propertyId: string;
  taskId: string;
  initialGuestSetupState?: Json | null;
  /** From checklist: host-defined staging lines (preferred over inventory guest rows). */
  stagingLines?: GuestAmenityStagingLine[] | null;
  /** From checklist: optional legacy host notes (shown when staging lines are empty). */
  hostGuestAmenitiesInstructions?: string | null;
  /** Arriving party size — drives leave-out math. Null/undefined → use minimum. */
  guestCount?: number | null;
  onCompleted: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  /** When true, use simple turnover chrome (header + progress). */
  simpleChrome?: boolean;
}

/**
 * Slice 44 — Cleaner stages leave-outs: giant qty + plain where line + proof photo.
 * Owner math never appears on this screen.
 */
export function GuestAmenitySetupStep({
  propertyId,
  taskId,
  initialGuestSetupState,
  stagingLines = null,
  hostGuestAmenitiesInstructions = null,
  guestCount = null,
  onCompleted,
  onBack,
  onSelectSegment,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = true,
  simpleChrome = false,
}: GuestAmenitySetupStepProps) {
  const { t, tDynamic } = useTranslation();
  const { data: items = [], isLoading: itemsLoading } = useInventoryForCleaner(propertyId);
  const { data: rooms = [], isLoading: roomsLoading } = useInventoryRoomsForCleaner(propertyId);
  const { data: photoGroups = [] } = usePhotoGroups(propertyId);
  const groupById = useMemo(() => new Map(photoGroups.map((g) => [g.id, g])), [photoGroups]);

  const useStaging = Boolean(stagingLines && stagingLines.length > 0);

  const guestItems = useMemo(() => {
    return (items as GuestItem[]).filter(
      (i) => i.inventory_scope !== "unit" && (!i.inventory_scope || i.inventory_scope === "guest_amenities"),
    );
  }, [items]);

  const roomNameById = useMemo(() => {
    const m = new Map<string, string>();
    rooms.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [rooms]);

  const knownGuestCount =
    typeof guestCount === "number" && Number.isFinite(guestCount) && guestCount >= 1
      ? Math.floor(guestCount)
      : null;
  const guestCountUnknown = knownGuestCount == null;

  const computedLines = useMemo(() => {
    if (!useStaging) return [];
    const out: {
      line: GuestAmenityStagingLine;
      leave_qty: number;
      used_minimum: boolean;
      where: string | null;
      photos: { name: string; photo_url: string }[];
      places: { qty: number; place: string; groupId: string | null }[];
    }[] = [];
    for (const line of stagingLines ?? []) {
      if (line.leave_out_plan) {
        const resolved = resolveLeaveOut(line.leave_out_plan, knownGuestCount);
        if (!resolved) continue;
        out.push({
          line,
          leave_qty: resolved.leaveQty,
          used_minimum: resolved.usedSmallest,
          where: resolved.where,
          photos: resolved.photos,
          places: resolved.spots,
        });
        continue;
      }
      const rule = effectiveLeaveOutRule(line);
      const { leave_qty, used_minimum } = computeLeaveOutQty(knownGuestCount, rule);
      if (leave_qty <= 0) continue;
      const roomLabel =
        line.room_name_snapshot?.trim() ||
        (line.property_room_id ? roomNameById.get(line.property_room_id) : null) ||
        null;
      const where = formatWhereLine(leave_qty, line.placements, roomLabel);
      out.push({ line, leave_qty, used_minimum, where, photos: [], places: [] });
    }
    return out;
  }, [useStaging, stagingLines, knownGuestCount, roomNameById]);

  const anyUsedMinimum = computedLines.some((c) => c.used_minimum);

  const segmentKeys = useMemo(() => {
    if (useStaging) return (stagingLines ?? []).map((l) => l.id);
    const keys = new Set<string>();
    guestItems.forEach((it) => {
      keys.add(it.room_id && it.room_id.trim() ? it.room_id : UNASSIGNED_KEY);
    });
    return Array.from(keys);
  }, [useStaging, stagingLines, guestItems]);

  const [proofUrlsByRoomId, setProofUrlsByRoomId] = useState<Record<string, string[]>>({});
  const [proofUrlsByStagingId, setProofUrlsByStagingId] = useState<Record<string, string[]>>({});
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    const parsed = parseGuestSetupState(initialGuestSetupState);
    setProofUrlsByRoomId(parsed.proof_urls_by_room_id ?? {});
    setProofUrlsByStagingId(parsed.proof_urls_by_staging_id ?? {});
  }, [taskId, initialGuestSetupState]);

  const uploadFiles = useCallback(
    async (segmentKey: string, files: File[], mode: "staging" | "legacy"): Promise<string[]> => {
      const urls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const prefix = mode === "staging" ? "staging" : "room";
        const path = `guest_setup/${propertyId}/${taskId}/${prefix}_${segmentKey.replace(/[^a-zA-Z0-9_-]/g, "_")}/${Date.now()}_${i}.${ext}`;
        const { error } = await supabase.storage.from("cleaning-photos").upload(path, file, {
          contentType: file.type || `image/${ext === "jpg" ? "jpeg" : ext}`,
          upsert: true,
        });
        if (error) {
          console.error(error);
          continue;
        }
        const { data: pub } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
        urls.push(pub.publicUrl);
      }
      return urls;
    },
    [propertyId, taskId],
  );

  const handlePickFiles = async (segmentKey: string, list: FileList | null, mode: "staging" | "legacy") => {
    if (!list?.length) return;
    setUploadingKey(segmentKey);
    try {
      const uploaded = await uploadFiles(segmentKey, Array.from(list), mode);
      if (uploaded.length === 0) {
        toast({ title: tDynamic("Upload failed"), variant: "destructive" });
        return;
      }
      if (mode === "staging") {
        setProofUrlsByStagingId((prev) => ({
          ...prev,
          [segmentKey]: [...(prev[segmentKey] ?? []), ...uploaded],
        }));
      } else {
        setProofUrlsByRoomId((prev) => ({
          ...prev,
          [segmentKey]: [...(prev[segmentKey] ?? []), ...uploaded],
        }));
      }
    } finally {
      setUploadingKey(null);
    }
  };

  const allProofDone = useMemo(() => {
    if (useStaging) {
      if (computedLines.length === 0) return true;
      return computedLines.every((c) => (proofUrlsByStagingId[c.line.id] ?? []).length > 0);
    }
    return segmentKeys.every((k) => (proofUrlsByRoomId[k] ?? []).length > 0);
  }, [useStaging, computedLines, proofUrlsByStagingId, segmentKeys, proofUrlsByRoomId]);

  const handleSubmit = async () => {
    if (!allProofDone) {
      toast({
        title: tDynamic("Photo required"),
        description: tDynamic("Add a proof photo for each item before continuing."),
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const left_out_by_staging_id: Record<string, number> = {};
      for (const c of computedLines) {
        left_out_by_staging_id[c.line.id] = c.leave_qty;
      }
      const guest_setup_state = useStaging
        ? {
            proof_urls_by_staging_id: proofUrlsByStagingId,
            left_out_by_staging_id,
            guest_count_used: knownGuestCount,
            guest_count_unknown: guestCountUnknown,
          }
        : {
            proof_urls_by_room_id: proofUrlsByRoomId,
            guest_count_used: knownGuestCount,
            guest_count_unknown: guestCountUnknown,
          };
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({
          guest_setup_completed: true,
          guest_setup_state,
        })
        .eq("id", taskId)
        .eq("property_id", propertyId);
      if (error) throw error;
      toast({
        title: tDynamic("Saved"),
        description: tDynamic("Continue to inventory counts."),
      });
      onCompleted();
    } catch (e) {
      console.error(e);
      toast({
        title: tDynamic("Error"),
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (itemsLoading || roomsLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
        {tDynamic("Loading...")}
      </div>
    );
  }

  if (!useStaging) {
    return null;
  }

  const chrome = {
    includeMaintenance,
    includeQuickCheck,
    includeGuestSetup,
  };

  const body = (
    <>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 px-1 pt-1">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e2eeed] text-[#15776d]">
              <Users className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <div className="min-w-0">
              <div className="text-[20px] font-bold tracking-[-0.3px] text-[#0f1720]">
                {knownGuestCount != null
                  ? t("gsNextStay").replace("{{n}}", String(knownGuestCount))
                  : tDynamic("Leave these out for guests")}
              </div>
              <div className="text-[14px] font-medium text-[#64748b]">{t("gsLeaveOutTap")}</div>
            </div>
          </div>

          {anyUsedMinimum ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-[#e6edf1] bg-white px-3.5 py-3">
              <div className="h-2 w-2 shrink-0 rounded-full bg-[#8494a2]" />
              <div className="text-sm font-medium text-[#475569]">
                {tDynamic("Guest count unknown — using the smallest stay")}
              </div>
            </div>
          ) : null}

          {computedLines.length === 0 ? (
            <div className="rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 py-4 text-[15px] font-medium text-[#64748b]">
              {tDynamic("Nothing to leave out for this stay.")}
            </div>
          ) : null}

          {computedLines.map(({ line, leave_qty, where, photos, places }) => {
            const proofUrls = proofUrlsByStagingId[line.id] ?? [];
            const hasProof = proofUrls.length > 0;
            const refUrl = line.host_reference_photo_url?.trim() || null;
            const subitems = line.subitems ?? [];
            const isKit = subitems.length > 0;

            return (
              <div
                key={line.id}
                className={cn(
                  "flex flex-col gap-3 rounded-[18px] border-[1.5px] bg-white px-3.5 py-3.5",
                  hasProof ? "border-[#24a89a]" : "border-[#e6edf1]",
                )}
              >
                <div className="flex items-center gap-3.5">
                <div className="min-w-12 shrink-0 text-center text-[48px] font-bold tabular-nums leading-none tracking-[-2px] text-[#15776d]">
                  {leave_qty}
                </div>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    className="block w-full text-left"
                    onClick={() => {
                      if (refUrl) setLightboxUrl(refUrl);
                    }}
                  >
                    <div className="truncate text-lg font-bold tracking-[-0.2px] text-[#0f1720]">
                      {tDynamic(line.item_label)}
                    </div>
                    {where && (places.length === 0 || places.length > 1) ? (
                      <div className="mt-0.5 text-[14.5px] font-medium leading-snug text-[#475569]">{tDynamic(where)}</div>
                    ) : null}
                    {isKit ? (
                      <div className="mt-1 text-[12px] font-medium text-[#8494a2]">
                        {tDynamic("Inside each kit")}:{" "}
                        {subitems
                          .filter((s) => s.item_label?.trim())
                          .map((s) => `${s.target_count}× ${tDynamic(s.item_label.trim())}`)
                          .join(", ")}
                      </div>
                    ) : null}
                  </button>
                </div>
                <div className="relative shrink-0">
                  <input
                    id={`guest-setup-staging-${line.id}`}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      void handlePickFiles(line.id, e.target.files, "staging");
                      e.target.value = "";
                    }}
                  />
                  {hasProof ? (
                    <button
                      type="button"
                      className="relative h-14 w-14 overflow-hidden rounded-[14px] border border-[#d7e0e6]"
                      onClick={() => document.getElementById(`guest-setup-staging-${line.id}`)?.click()}
                      disabled={submitting || uploadingKey === line.id}
                      aria-label={tDynamic("Retake photo")}
                    >
                      <img src={proofUrls[0]} alt="" className="h-full w-full object-cover" />
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#24a89a] text-white shadow">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="flex h-[60px] w-[60px] flex-col items-center justify-center gap-0.5 rounded-[14px] bg-[#e2eeed] text-[#15776d] active:scale-95"
                      onClick={() => document.getElementById(`guest-setup-staging-${line.id}`)?.click()}
                      disabled={submitting || uploadingKey === line.id}
                    >
                      {uploadingKey === line.id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <Camera className="h-[18px] w-[18px]" strokeWidth={2.2} />
                          <span className="text-[10px] font-bold">{tDynamic("Photo")}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                </div>
                {places.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {places.map((pl, i) => {
                      const g = pl.groupId ? groupById.get(pl.groupId) : null;
                      const name = g?.name ?? pl.place;
                      const url = g?.reference_photo_url ?? null;
                      return (
                        <button
                          key={`${pl.place}-${i}`}
                          type="button"
                          disabled={!url}
                          onClick={() => url && setLightboxUrl(url)}
                          className="flex min-h-[48px] items-center gap-2 rounded-[14px] bg-[#f4f7f9] py-1.5 pl-1.5 pr-3.5 text-left"
                        >
                          {url ? (
                            <img src={url} alt="" className="h-9 w-9 shrink-0 rounded-[9px] object-cover" />
                          ) : (
                            <span className="h-9 w-9 shrink-0 rounded-[9px] bg-[repeating-linear-gradient(135deg,#e6edf1_0_6px,#f4f7f9_6px_12px)]" />
                          )}
                          <span className="text-[14.5px] font-bold text-[#0f1720]">{tDynamic(name)}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {photos.length > 0 ? (
                  <div className="flex gap-2">
                    {photos.map((photo) => (
                      <button
                        key={photo.photo_url}
                        type="button"
                        className="relative h-[116px] min-w-0 flex-1 overflow-hidden rounded-xl"
                        onClick={() => setLightboxUrl(photo.photo_url)}
                      >
                        <img src={photo.photo_url} alt="" className="h-full w-full object-cover" />
                        <span className="absolute bottom-2 left-2 rounded-full bg-white px-2.5 py-1 text-[13px] font-bold text-[#0f1720]">
                          {photo.name}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
    </>
  );

  if (simpleChrome) {
    return (
      <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative text-[#0f1720]")}>
        <SimpleStepHeader
          title={t("segGuest") !== "segGuest" ? t("segGuest") : "Guest"}
          activeSegment={simpleFlowSegmentIndex("guest_setup", chrome)}
          includeMaintenance={includeMaintenance}
          includeQuickCheck={includeQuickCheck}
          includeGuestSetup={includeGuestSetup}
          onBack={onBack}
          onSelectSegment={onSelectSegment}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">{body}</div>
        <div className={CLEANER_SIMPLE_CTA_FOOTER}>
          <Button
            type="button"
            className="h-14 w-full rounded-[16px] text-[17px] font-bold"
            disabled={!allProofDone || submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
            {tDynamic("Next")}
          </Button>
        </div>
        {lightboxUrl ? (
          <CleanerMediaLightbox
            open={Boolean(lightboxUrl)}
            onOpenChange={(open) => {
              if (!open) setLightboxUrl(null);
            }}
            urls={[lightboxUrl]}
            initialIndex={0}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {body}
      <Button
        type="button"
        className="h-12 w-full"
        disabled={!allProofDone || submitting}
        onClick={() => void handleSubmit()}
      >
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {tDynamic("Next")}
      </Button>
      {lightboxUrl ? (
        <CleanerMediaLightbox
          open={Boolean(lightboxUrl)}
          onOpenChange={(open) => {
            if (!open) setLightboxUrl(null);
          }}
          urls={[lightboxUrl]}
          initialIndex={0}
        />
      ) : null}
    </div>
  );
}

/** Build Inv recall line from staging + saved left-out counts. */
export function buildLeftOutRecallFromStaging(
  stagingLines: GuestAmenityStagingLine[] | null | undefined,
  leftOutById: Record<string, number> | null | undefined,
  guestCount: number | null | undefined,
): string | null {
  if (!stagingLines?.length) return null;
  const parts: string[] = [];
  for (const line of stagingLines) {
    const saved = leftOutById?.[line.id];
    const leave =
      typeof saved === "number"
        ? saved
        : computeLeaveOutQty(guestCount, effectiveLeaveOutRule(line)).leave_qty;
    if (leave <= 0) continue;
    parts.push(`${leave} ${shortLeaveOutLabel(line.item_label)}`);
  }
  if (parts.length === 0) return null;
  return `You left out: ${parts.join(" · ")}`;
}
